import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import './calendar.scss';

import CalendarHeader from './CalendarHeader';
import { useInitializeDays, useScreenWidth } from '../../shared/Helpers/hooks';
import MonthView from './monthView';
import WeekView from './weekView';
import DayView from './dayView';
import CalendarPopups from './Actions';
import CalendarGadget from './Session/calendarGadget';
import AddSession from './Actions/addSession';
import RequestCancellation from './Actions/requestTimeOff';
import AdminSetCancellation from './Actions/adminSetTimeOff';

import { DispatchProperties, useSelector } from '../../redux/store';
import { clearEvents, getEvents } from '../../redux/State/clientSlice/calendarSlice';
import { clearActiveTargets } from '../../redux/State/clientSlice/programTargetSlice';
import { getRoleConfigruation } from '../../redux/State/identitySlice/accountSlice';
import { AdminTypes } from '../../components/Menu';
import { getAdminHoursUsage } from '../../redux/State/userSlice/userSessionAllocationSlice';

const CalendarView = () => {
	useInitializeDays();

	const dispatch = useDispatch<DispatchProperties>();
	const screenWidth = useScreenWidth();
	const isSmallScreen = screenWidth >= 1024 && screenWidth <= 1600;

	const isMenuOpen = useSelector((state) => state.common.isMenuOpen);
	const view = useSelector((state) => state.calendarHelper.calendarView);
	const filterData = useSelector((state) => state.calendarHelper.filterData);
	const applyFilters = useSelector((state) => state.calendarHelper.filterData.applyFilters);
	const includeBTEvents = useSelector((state) => state.calendarHelper.filterData.includeBTEvents);
	const includeBCBAEvents = useSelector((state) => state.calendarHelper.filterData.includeBCBAEvents);
	const addEventOpen = useSelector((state) => state.calendarHelper.popups.addScheduled);
	const addDiscretionaryOpen = useSelector((state) => state.calendarHelper.popups.addDiscretionary);
	const days = useSelector((state) => state.calendarHelper.displayedCalendarPageDays);
	const month = useSelector((state) => state.calendarHelper.month);
	const year = useSelector((state) => state.calendarHelper.year);
	const role = useSelector((state) => state.account.role);
	const actorId = useSelector((state) => state.account.user.id);
	const editHoursOpen = useSelector((state) => state.calendarHelper.popups.editDiscretionary);
	useEffect(() => {
		if (actorId && !addDiscretionaryOpen && !editHoursOpen) dispatch(getAdminHoursUsage({ userId: actorId, date: `${year}-${String(month + 1).padStart(2, '0')}-01` }));
	}, [dispatch, actorId, month, year, addDiscretionaryOpen, editHoursOpen]);

	const clientId = filterData.client?.id;
	const userId = filterData.therapist?.id;
	const canFilterEvents = role?.section.id === AdminTypes.admin || role?.section.id === AdminTypes.cs || role?.section.id === AdminTypes.bcba || role?.section.id === AdminTypes.bt;

	useEffect(() => {
		dispatch(clearActiveTargets());
		dispatch(getRoleConfigruation());

		return () => {
			dispatch(clearEvents());
		};
	}, [dispatch]);

	useEffect(() => {
		const hasCurrentMonthDays = days.some((day) => day.status === 1 && day.month === month && day.year === year);
		if (!hasCurrentMonthDays || addEventOpen || addDiscretionaryOpen || applyFilters) return;

		const eventData = canFilterEvents
			? { days, clientId, userId, includeBTEvents, includeBCBAEvents }
			: { days, includeBTEvents, includeBCBAEvents };
		dispatch(getEvents(eventData));
	}, [
		dispatch,
		clientId,
		userId,
		days,
		month,
		year,
		includeBCBAEvents,
		includeBTEvents,
		addEventOpen,
		addDiscretionaryOpen,
		applyFilters,
		canFilterEvents,
	]);

	const isSidebarHiddenByCSS = screenWidth <= 1024;
	const displayCalendarGadget = isSidebarHiddenByCSS ? false : isSmallScreen ? !isMenuOpen : true;

	return (
		<div className="calendar">
			{displayCalendarGadget && (
				<div className="datePickerCard">
					<CalendarGadget />
					<AddSession />
					<RequestCancellation />
					<AdminSetCancellation />
				</div>
			)}
			<div className="calendarCard">
				<CalendarHeader showSessionControls={!displayCalendarGadget} />
				{view === 0 && <MonthView />}
				{view === 1 && <WeekView />}
				{view === 2 && <DayView />}
			</div>
		</div>
	);
};
const Calendar = () => (
	<>
		<CalendarView />
		<CalendarPopups />
	</>
);

export default Calendar;
